import { useState } from 'react';
import { ExternalLink, Play, FileText, Music, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import {
  extractGoogleDriveFileId,
  isGoogleDriveUrl,
  getGoogleDriveEmbedUrl,
  getGoogleDriveThumbnail,
  validateGoogleDriveUrl,
} from '../utils/googleDrive';

/**
 * Preview component for Google Drive content
 * Shows embedded preview or thumbnail based on content type
 */
const GoogleDrivePreview = ({ url, type = 'video', onValidation }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  const validation = validateGoogleDriveUrl(url);
  const isGDrive = isGoogleDriveUrl(url);
  const fileId = extractGoogleDriveFileId(url);
  const embedUrl = getGoogleDriveEmbedUrl(url);
  const thumbnailUrl = getGoogleDriveThumbnail(url, 400);

  // Notify parent of validation status
  if (onValidation && validation.valid !== undefined) {
    onValidation(validation.valid);
  }

  if (!url) {
    return (
      <div className="border border-dashed border-border rounded-lg p-6 text-center">
        <p className="text-muted-foreground text-sm">Enter a URL to see preview</p>
      </div>
    );
  }

  if (!validation.valid) {
    return (
      <div className="border border-red-500/30 bg-red-500/10 rounded-lg p-4 flex items-center gap-3">
        <AlertCircle size={20} className="text-red-500" />
        <p className="text-red-400 text-sm">{validation.error}</p>
      </div>
    );
  }

  // Non-Google Drive URL (YouTube, Vimeo, etc.)
  if (!isGDrive) {
    return (
      <div className="border border-border rounded-lg p-4">
        <div className="flex items-center gap-3 mb-3">
          <CheckCircle size={20} className="text-green-500" />
          <p className="text-sm text-green-400">Valid external URL</p>
        </div>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm flex items-center gap-2 hover:underline"
          style={{ color: '#D4AF37' }}
        >
          <ExternalLink size={16} />
          Open in new tab
        </a>
      </div>
    );
  }

  // Google Drive URL
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="p-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CheckCircle size={16} className="text-green-500" />
          <span className="text-xs text-green-400">Google Drive URL detected</span>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setShowPreview(!showPreview)}
          className="text-xs"
        >
          {showPreview ? 'Hide Preview' : 'Show Preview'}
        </Button>
      </div>

      {showPreview && (
        <div className="relative bg-black/50">
          {type === 'video' && (
            <div className="aspect-video">
              <iframe
                src={embedUrl}
                className="w-full h-full"
                allow="autoplay; encrypted-media"
                allowFullScreen
                title="Video Preview"
                onError={() => setPreviewError(true)}
              />
            </div>
          )}

          {type === 'pdf' && (
            <div className="aspect-[4/3]">
              <iframe
                src={embedUrl}
                className="w-full h-full"
                title="PDF Preview"
                onError={() => setPreviewError(true)}
              />
            </div>
          )}

          {type === 'audio' && (
            <div className="p-6 flex flex-col items-center gap-4">
              <Music size={48} style={{ color: '#D4AF37' }} />
              <audio controls className="w-full max-w-md">
                <source src={`https://drive.google.com/uc?export=download&id=${fileId}`} />
                Your browser does not support the audio element.
              </audio>
              <p className="text-xs text-muted-foreground">
                Note: Audio preview may require file to be publicly accessible
              </p>
            </div>
          )}

          {previewError && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80">
              <div className="text-center">
                <AlertCircle size={32} className="text-yellow-500 mx-auto mb-2" />
                <p className="text-sm text-yellow-400">Preview unavailable</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Make sure the file is set to &quot;Anyone with link can view&quot;
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="p-3 text-xs text-muted-foreground">
        <p>File ID: {fileId}</p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 mt-1 hover:underline"
          style={{ color: '#D4AF37' }}
        >
          <ExternalLink size={12} />
          Open in Google Drive
        </a>
      </div>
    </div>
  );
};

export default GoogleDrivePreview;
